import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import PublicIcon from '@mui/icons-material/Public';

const ButtPayLanding = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Instant Settlement ⚡',
      description: 'Transfer money globally in under 500ms with near-zero fees'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'USDC-Backed 💰',
      description: 'Every dollar is 1:1 backed by USDC in qualified custody'
    },
    {
      icon: <PublicIcon sx={{ fontSize: 40 }} />,
      title: 'Global Access 🌍',
      description: 'Financial inclusion for creators, freelancers, and businesses worldwide'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Rewards Program 🍑',
      description: 'Earn cashback on every transaction, credited as USDC'
    }
  ];

  const balanceComponents = [
    { component: 'Ledger ID', description: 'Unique on-chain/off-chain hybrid identifier' },
    { component: 'Denomination', description: 'USD display, 1:1 backed by USDC' },
    { component: 'State Variables', description: 'Available Balance, Held Collateral, Pending Settlement' },
    { component: 'Events', description: 'Deposit, Purchase, Sale, Transfer, Hold, Reward' }
  ];

  const revenueStreams = [
    { stream: 'Trading Spread 💰', description: 'USDC buy/sell (15–25 bps each side)', spread: '0.3–0.5%' },
    { stream: 'Interchange Share 💳', description: 'Credit card transactions', spread: '0.6–1.0%' },
    { stream: 'Custody Float Yield 📈', description: 'Short-term T-Bill yields on fiat buffers', spread: '2–4% APY' },
    { stream: 'FX Edges 🌐', description: 'Local currency redemptions', spread: '50–100 bps' }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom fontWeight="bold">
            🍑 ButtPay 💰
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.9 }}>
            Blitz Universal Transmitter & Treasury
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, maxWidth: '800px', mx: 'auto', opacity: 0.85 }}>
            A unified financial infrastructure designed to abstract away the complexity of global money movement
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={() => navigate('/onboard')}
            >
              Get Started 🚀
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
              onClick={() => window.open('https://docs.google.com/document/d/1AVIdsWcoS252eCWejPdzX4MZ73WtsbRLROQBcc2VCzY/edit?usp=sharing', '_blank')}
            >
              Read Whitepaper 📄
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Founder Section - Moved to Top */}
        <Paper 
          elevation={4} 
          sx={{ 
            p: 5, 
            mb: 6,
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: 3
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" align="center" sx={{ mb: 4 }}>
            Meet the Founder 🍑
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Avatar
                src="https://storage.googleapis.com/blitzccmc.appspot.com/1/IMG_3232.JPG"
                alt="Alex Roa"
                sx={{
                  width: 200,
                  height: 200,
                  border: '5px solid white',
                  boxShadow: 3
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Alex Roa
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.95 }}>
                Founder, CEO/CTO 💼
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                Visionary entrepreneur building the future of global finance. Combining deep technical 
                expertise with a passion for financial inclusion, Alex is revolutionizing how the world 
                moves money—one ButtPay at a time. 💰🍑
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Abstract Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
            Abstract
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            ButtPay (Blitz Universal Transmitter & Treasury) is a unified financial infrastructure
            designed to abstract away the complexity of global money movement. Built on USDC-denominated
            ledgers, it enables instant, low-cost value exchange, on-demand liquidity, and secured credit
            issuance without traditional banking intermediaries. 💰
          </Typography>
        </Paper>

        {/* Features Grid */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          Key Features ✨
        </Typography>
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* The Problem */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            The Problem 😤
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Money today is trapped inside a fragmented, jurisdictional web:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1" paragraph>Cross-border transfers take days and cost 3–7% in fees.</Typography></li>
            <li><Typography variant="body1" paragraph>Freelancers, creators, and gig workers in emerging markets face limited banking access.</Typography></li>
            <li><Typography variant="body1" paragraph>Businesses are forced to maintain multiple rails and compliance stacks for each region.</Typography></li>
            <li><Typography variant="body1" paragraph>Credit availability remains tethered to outdated collateralization and slow underwriting.</Typography></li>
          </Box>
          <Typography variant="h6" fontWeight="bold">
            The result: friction, exclusion, and latency in a world where digital work is borderless. 🌍
          </Typography>
        </Paper>

        {/* The ButtPay Thesis */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            The ButtPay Thesis 🍑
          </Typography>
          <Typography variant="h5" sx={{ fontStyle: 'italic', mb: 3 }}>
            "If value can be represented digitally, then the world's treasury should flow as instantly as information." 💡
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            ButtPay proposes a unified balance layer—the Blitz Balance—that acts as a programmable,
            composable money primitive. By modeling money as tokenized liabilities settled through the
            purchase and sale of USDC, ButtPay achieves near-instant liquidity without assuming the legal
            role of a money transmitter. 💰
          </Typography>
        </Paper>

        {/* System Architecture */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          System Architecture 🏗️
        </Typography>

        {/* Blitz Balance Components */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            The Blitz Balance ⚡💰
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            At the user level, every account holds a Blitz Balance—a USD-denominated representation
            of USDC held in qualified custody.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Component</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balanceComponents.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.component}</TableCell>
                    <TableCell>{row.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Secured Charge Card */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Secured Charge Card 💳
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The ButtPay Card extends Blitz Balance utility into the physical and digital commerce world. 🍑
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Typography variant="body1" fontWeight="bold">Secured Charge Card</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Collateral</Typography>
                <Typography variant="body1" fontWeight="bold">USDC-backed Blitz Balance</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">LTV Limit</Typography>
                <Typography variant="body1" fontWeight="bold">85–90%</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Rewards</Typography>
                <Typography variant="body1" fontWeight="bold">1% USDC Cashback 💰</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Economic Model */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          Economic Model 📊
        </Typography>
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Revenue Stream</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Est. Spread</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueStreams.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.stream}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">
                      <Chip label={row.spread} color="primary" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Global Impact */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            The Global Impact 🌍
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Financial Inclusion 🍑
              </Typography>
              <Typography variant="body2">
                A Nigerian gig-worker or Filipino designer can hold stable USD exposure, spend globally
                with Visa/Mastercard, and receive earnings instantly in USDC—no bank required.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Cross-Border Efficiency ⚡
              </Typography>
              <Typography variant="body2">
                Traditional remittances = T+3 days + 7% fees.<br />
                ButtPay = T+0 + &lt;0.5% spread. 💰
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Network Effects 🚀
              </Typography>
              <Typography variant="body2">
                Every enterprise using BlitzPay feeds liquidity into ButtPay Treasury.
                Treasury scale drives down spreads and boosts card reward economics.
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Vision */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
            Vision 2026–2030 🔮
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1" paragraph><strong>Full Global Access:</strong> Non-U.S. users onboarded via compliant local partners. 🌍</Typography></li>
            <li><Typography variant="body1" paragraph><strong>Real-Time FX:</strong> Dynamic conversion USDC ↔ EURC, JPY tokens. 💱</Typography></li>
            <li><Typography variant="body1" paragraph><strong>Network Lending:</strong> Over-collateralized credit lines on top of ButtPay balances. 💰</Typography></li>
            <li><Typography variant="body1" paragraph><strong>Open API Layer:</strong> White-label treasury for enterprises and governments. 🔧</Typography></li>
            <li><Typography variant="body1" paragraph><strong>Regulated Digital Bank Charter:</strong> "Blitz Bank" (optional). 🏦</Typography></li>
          </Box>
        </Paper>

        {/* Conclusion */}
        <Paper
          elevation={4}
          sx={{
            p: 5,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Conclusion 🍑💰
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            ButtPay unifies three historically separate domains: Payments infrastructure,
            Treasury management, and Consumer banking UX. By grounding value in USDC and
            orchestrating through qualified custodians and partner banks, ButtPay eliminates
            friction, democratizes access, and turns digital work into a globally spendable currency.
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            Re-inventing how the world moves money—one ButtPay at a time. 🚀
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                mr: 2,
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={() => navigate('/onboard')}
            >
              Join the Revolution 🍑
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            © 2025 ButtPay - Blitz Universal Transmitter & Treasury. All rights reserved. 🍑💰
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            <a href="https://www.buttpay.com" style={{ color: 'white' }}>www.buttpay.com</a>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default ButtPayLanding;